import React, { useState } from 'react';
import styles from './ProjectTemplatesSettings.module.css';
import { 
  FileText, 
  GitBranch, 
  Users, 
  Settings,
  Lightbulb,
  ClipboardList,
  Layers,
  DraftingCompass,
  GitMerge,
  Factory,
  Cog,
  X,
  ExternalLink
} from 'lucide-react';

const ProjectTemplatesSettings = () => {
  const [defaultTemplate, setDefaultTemplate] = useState('system-v-diagram');
  const [orgElements, setOrgElements] = useState([
    { icon: <Cog size={16} />, color: '#ef4444', text: 'Engineering' },
    { icon: <Users size={16} />, color: '#22c55e', text: 'Customer Success' }
  ]);

  const [customElements, setCustomElements] = useState([
    { icon: <Cog size={16} />, color: '#ef4444', text: 'Custom 1' },
    { icon: <Users size={16} />, color: '#22c55e', text: 'Custom 2' }
  ]);

  const systemVPhases = [
    { icon: <Lightbulb size={16} />, color: '#ef4444', text: 'Concept Exploration' },
    { icon: <ClipboardList size={16} />, color: '#f97316', text: 'Requirements' },
    { icon: <Layers size={16} />, color: '#eab308', text: 'Architecture' },
    { icon: <DraftingCompass size={16} />, color: '#22c55e', text: 'Detailed Design' },
    { icon: <GitMerge size={16} />, color: '#06b6d4', text: 'Integration & Testing' },
    { icon: <Factory size={16} />, color: '#3b82f6', text: 'Manufacturing' },
    { icon: <Settings size={16} />, color: '#8b5cf6', text: 'Operations & Maintenance' }
  ];

  const handleOrgElementChange = (index, field, value) => {
    const newElements = [...orgElements];
    newElements[index] = { ...newElements[index], [field]: value };
    setOrgElements(newElements);
  };

  const handleCustomElementChange = (index, field, value) => {
    const newElements = [...customElements];
    newElements[index] = { ...newElements[index], [field]: value };
    setCustomElements(newElements);
  };

  const addOrgElement = () => {
    setOrgElements([...orgElements, { icon: <Cog size={16} />, color: '#6b7280', text: 'New Element' }]);
  };

  const addCustomElement = () => {
    setCustomElements([...customElements, { icon: <Cog size={16} />, color: '#6b7280', text: 'New Element' }]);
  };

  const removeOrgElement = (index) => {
    if (orgElements.length > 1) {
      setOrgElements(orgElements.filter((_, i) => i !== index));
    }
  };

  const removeCustomElement = (index) => {
    if (customElements.length > 1) {
      setCustomElements(customElements.filter((_, i) => i !== index));
    }
  };

  const handleMakeDefault = (templateId) => {
    setDefaultTemplate(templateId);
  };

  const templates = [
    {
      id: 'blank',
      name: 'Blank Template',
      description: 'Start your project from scratch with a clean slate.',
      icon: <FileText size={24} />,
      type: 'blank'
    },
    {
      id: 'system-v-diagram',
      name: 'System V Diagram',
      description: (
        <>
          A structured approach to Systems Engineering using the{' '}
          <a 
            href="https://en.wikipedia.org/wiki/V-model" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.externalLink}
          >
            V-Model
            <ExternalLink size={12} className={styles.externalLinkIcon} />
          </a>
          .
        </>
      ),
      icon: <GitBranch size={24} />,
      type: 'fixed',
      phases: systemVPhases
    },
    {
      id: 'org-chart',
      name: 'Org Chart Template',
      description: 'Customize project elements based on your organizational structure.',
      icon: <Users size={24} />,
      type: 'editable',
      elements: orgElements,
      onElementChange: handleOrgElementChange,
      onAddElement: addOrgElement,
      onRemoveElement: removeOrgElement
    },
    {
      id: 'custom',
      name: 'Custom Template',
      description: 'Define your own project structure and elements to fit unique needs.',
      icon: <Settings size={24} />,
      type: 'editable',
      elements: customElements,
      onElementChange: handleCustomElementChange,
      onAddElement: addCustomElement,
      onRemoveElement: removeCustomElement
    }
  ];

  const renderBlankTemplate = () => (
    <div className={styles.templateOutline}>
      <div className={styles.outlineHeader}>
        <h3>Project Outline</h3>
      </div>
      <div className={styles.blankOutline}>
        <div className={styles.blankIcon}>
          <FileText size={48} />
        </div>
        <p>Start with a blank canvas</p>
        <p className={styles.blankSubtext}>Add your own phases, milestones, and structure</p>
      </div>
    </div>
  );

  const renderSystemVTemplate = (phases) => (
    <div className={styles.templateOutline}>
      <div className={styles.outlineHeader}>
        <h3>Project Outline</h3>
        <span className={styles.phaseCount}>{phases.length} phases</span>
      </div>
      <div className={styles.phasesList}>
        {phases.map((phase, index) => (
          <div key={index} className={styles.phaseRow}>
            <div 
              className={styles.phaseIcon} 
              style={{ backgroundColor: phase.color + '20', color: phase.color }}
            >
              {phase.icon}
            </div>
            <span className={styles.phaseText}>{phase.text}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderEditableTemplate = (template, elements, onElementChange, onAddElement, onRemoveElement) => (
    <div className={styles.templateOutline}>
      <div className={styles.outlineHeader}>
        <h3>Customizable Elements</h3>
        <button className={styles.addElementButton} onClick={onAddElement}>
          + Add Element
        </button>
      </div>
      <div className={styles.elementsList}>
        {elements.map((element, index) => (
          <div key={index} className={styles.elementRow}>
            <div className={styles.elementIcon}>
              {element.icon}
            </div>
            <input
              type="color"
              value={element.color}
              onChange={(e) => onElementChange(index, 'color', e.target.value)}
              className={styles.colorPicker}
            />
            <input
              type="text"
              value={element.text}
              onChange={(e) => onElementChange(index, 'text', e.target.value)}
              className={styles.textInput}
              placeholder="Element name"
            />
            <button 
              className={styles.removeButton}
              onClick={() => onRemoveElement(index)}
              disabled={elements.length <= 1}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTemplateOutline = (template) => {
    switch (template.type) {
      case 'blank':
        return renderBlankTemplate();
      case 'fixed':
        return renderSystemVTemplate(template.phases);
      case 'editable':
        return renderEditableTemplate(
          template,
          template.elements,
          template.onElementChange,
          template.onAddElement,
          template.onRemoveElement
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.projectTemplatesSettings}>
      <div className={styles.header}>
        <h1>Project Templates</h1>
        <p className={styles.subtitle}>Customize the templates available for your projects.</p>
      </div>
      <div className={styles.templatesGrid}>
        {templates.map((template) => (
          <div key={template.id} className={`${styles.templateCard} ${defaultTemplate === template.id ? styles.defaultCard : ''}`}>
            <div className={styles.cardHeader}>
              <div className={styles.titleSection}>
                {template.icon}
                <h2>{template.name}</h2>
              </div>
            </div>
            <p className={styles.cardDescription}>{template.description}</p>
            
            {renderTemplateOutline(template)}
            
            {defaultTemplate === template.id ? (
              <div className={styles.currentDefaultBanner}>
                Current Default
              </div>
            ) : (
              <button 
                className={styles.makeDefaultButton}
                onClick={() => handleMakeDefault(template.id)}
              >
                Make Default
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectTemplatesSettings;
